// Angular
import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Third party
import moment from 'moment';

// Local library
import {
  IActivityBetModel,
  ICustomerActivityModel,
  IGetAccountReportExRequest
} from '@amwager/util-ts';

/**
 * A "Totals" row (Type / Current) in the Overview panel.
 */
export interface IOverviewTotalRow {
  label: string;
  value: number;
}

/**
 * A Best/Worst bet row with derived win/loss figures.
 */
export interface IOverviewBetRow {
  eventName: string;
  poolId: string;
  wagered: number;
  winning: number;
  winLoss: number;        // winning - wagered
  winLossPercent: number; // winLoss as % of wagered
}

/**
 * Pure form + calculation helpers for the Overview view. Mirrors
 * BetSummaryService; ported from the legacy statements Overview tab.
 */
@Injectable({ providedIn: 'root' })
export class OverviewService {
  private readonly formBuilder = inject(FormBuilder);

  /**
   * Build the reactive form (date range only)
   * @returns {FormGroup} form with dateFrom, dateTo
   */
  buildForm(): FormGroup {
    return this.formBuilder.group({
      dateFrom: [moment().subtract(1, 'days').toDate(), [Validators.required]],
      dateTo: [new Date(), [Validators.required]]
    });
  }

  /**
   * Map the form state to an overview API request
   * @param {FormGroup} form The overview form
   * @returns {IGetAccountReportExRequest} Request payload
   */
  buildRequest(form: FormGroup): IGetAccountReportExRequest {
    const value = form.getRawValue();
    return {
      startDate: value.dateFrom,
      endDate: value.dateTo
    };
  }

  /**
   * True when "Date From" is after "Date To"
   * @param {FormGroup} form The overview form
   * @returns {boolean} Whether the range is invalid
   */
  isDateRangeInvalid(form: FormGroup): boolean {
    const from = form.get('dateFrom')?.value;
    const to = form.get('dateTo')?.value;
    if (!from || !to) return false;
    return moment(from).isAfter(moment(to));
  }

  /**
   * Sum daily activity into the Totals rows
   * @param {ICustomerActivityModel[]} activity Daily activity records
   * @returns {IOverviewTotalRow[]} Rows, or [] when there is no activity
   */
  buildTotals(activity: ICustomerActivityModel[]): IOverviewTotalRow[] {
    // No activity → no rows (legacy shows "No Data to display")
    if (!activity.length) return [];

    const sum = (pick: (a: ICustomerActivityModel) => number): number =>
      activity.reduce((total, record) => total + pick(record), 0);

    const winLoss = sum(
      (a) => (a.Reward ?? 0) + (a.Winning ?? 0) - (a.Wagered ?? 0)
    );

    return [
      { label: 'Deposited', value: sum((a) => a.DespostiAmount ?? 0) },
      { label: 'Wagered', value: sum((a) => a.Wagered ?? 0) },
      { label: 'Winnings', value: sum((a) => a.Winning ?? 0) },
      { label: 'Rewards', value: sum((a) => a.Reward ?? 0) },
      { label: 'WIN/LOSS', value: winLoss }
    ];
  }

  /**
   * Sort + map best/worst bets into display rows
   * @param {IActivityBetModel[]} bets Raw bet rows
   * @param {boolean} bestFirst True = highest P/L first (best), false = lowest first (worst)
   * @returns {IOverviewBetRow[]} Rows with derived win/loss + percent
   */
  buildBets(bets: IActivityBetModel[], bestFirst: boolean): IOverviewBetRow[] {
    return [...bets]
      .sort((a, b) => {
        const plA = (a.Winning ?? 0) - (a.Wagered ?? 0);
        const plB = (b.Winning ?? 0) - (b.Wagered ?? 0);
        return bestFirst ? plB - plA : plA - plB;
      })
      .map((bet) => {
        const wagered = bet.Wagered ?? 0;
        const winning = bet.Winning ?? 0;
        const winLoss = winning - wagered;
        return {
          eventName: bet.EventName ?? '',
          poolId: bet.PoolId ?? '',
          wagered,
          winning,
          winLoss,
          winLossPercent: wagered ? (winLoss / wagered) * 100 : 0
        };
      });
  }
}
